import {
  Box,
  Button,
  Checkbox,
  Container,
  Dialog,
  FormControlLabel,
  Grid,
  IconButton,
  Slide,
  Slider,
  TextField,
  Toolbar,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LogoutIcon from "@mui/icons-material/Logout";
import { AppBar } from "../styled-components/AppBar";
import { forwardRef, useEffect, useState } from "react";
import { fetchViaPost } from "../helpers/fetchViaPost";
import { styled } from "@mui/material/styles";
import Autocomplete from "@mui/material/Autocomplete";
import YoutubeModal from "./YoutubeModal";
import i18n, { setBettyLanguage } from "../i18n";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export function UserDialog({ open, setOpen, user, allUsers, setAllUsers }) {
  const isAdmin =
    user?.id === "paule@paule-wetten.de";


  const handleChangeTutorialActive = (event) => {
    localStorage.setItem("showTutorials", event.target.checked);
    setTutorialActive(event.target.checked);
  };

  async function syncUsers() {
    if (!user) {
      setAllUsers([]);
      return;
    }
    if (isAdmin) {
      const data = await (
        await fetchViaPost("get-all-users", JSON.stringify({}))
      ).json();
      setAllUsers(data.users);
    } else {
      setAllUsers([]);
    }
  }

  async function getUserToken(userId) {
    const data = await (
      await fetchViaPost(
        "get-user-token",
        JSON.stringify({ search_user_id: userId }),
      )
    ).json();
    var win = window.open(data, "_blank");
    win.focus();
  }

  function handleClose() {
    setOpen(false);
  }

  function handleLogout() {
    document.location = "/sign-in";
  }

  async function handleSave(e) {
    e?.preventDefault?.();

    const data = new FormData(e.currentTarget);

    const settings = {
      ...(data.get("name") === user.name ? {} : { name: data.get("name") }),
      mainUpdateFactor: noMainUpdateFactor
        ? Number.MAX_SAFE_INTEGER
        : -data.get("mainUpdateFactor"),
      drawUpdateFactor: noDrawUpdateFactor
        ? Number.MAX_SAFE_INTEGER
        : -data.get("drawUpdateFactor"),
      language,
    };

    await fetchViaPost("save-settings", JSON.stringify(settings));

    setOpen(false);
  }

  const [isDisabled, setIsDisabled] = useState(false);
  const [tutorialActive, setTutorialActive] = useState(
    localStorage.getItem("showTutorials") === "true",
  );
  const [noMainUpdateFactor, setNoMainUpdateFactor] = useState(false);
  const [noDrawUpdateFactor, setNoDrawUpdateFactor] = useState(false);
  const [language, setLanguage] = useState(user?.language || "de");

  useEffect(() => {
    syncUsers();
    setNoMainUpdateFactor(user?.main_update_factor === Number.MAX_SAFE_INTEGER);
    setNoDrawUpdateFactor(user?.draw_update_factor === Number.MAX_SAFE_INTEGER);

    const savedLanguage = user?.language || "de";
    setLanguage(savedLanguage);
    setBettyLanguage(savedLanguage);
  }, [user]);

  if (!(user && user.main_update_factor && user.draw_update_factor))
    return <></>;

  const Div = styled("div")(({ theme }) => ({
    ...theme.typography.button,
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(1),
  }));

  function getCurrentPackage(user) {
    if (user?.plan === "gold") return "GOLD";
    if (user?.plan === "silver") return "SILVER";
    return "FREE";
  }

  async function handleSilverSubscription() {
    try {
      const response = await fetchViaPost(
        "create-paypal-subscription",
        JSON.stringify({})
      );
      const result = await response.json();

      if (!result?.ok || !result?.approvalUrl) {
        throw new Error("No PayPal approval URL returned");
      }

      window.location.href = result.approvalUrl;
    } catch (error) {
      console.error("Silver subscription start failed:", error);
      alert(
        "Silver subscription could not be started. Please try again later."
      );
    }
  }

  function printPaypalSubscriptionText() {
    if (getCurrentPackage(user) === "FREE") {
      return (
        <>
          <br />
          Please{" "}
          <button
            type="button"
            onClick={handleSilverSubscription}
            style={{
              border: 0,
              padding: 0,
              background: "none",
              color: "#0000ee",
              textDecoration: "underline",
              cursor: "pointer",
              font: "inherit",
            }}
          >
            Subscribe
          </button>{" "}
          to upgrade your package
        </>
      );
    } else return <></>;
  }

  // From https://bitbucket.org/atlassian/atlaskit-mk-2/raw/4ad0e56649c3e6c973e226b7efaeb28cb240ccb0/packages/core/select/src/data/countries.js
  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
    >
      <form onSubmit={handleSave}>
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleClose}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5em",
                }}
              >
                Settings
              </div>
            </Typography>
            <Button
              color="inherit"
              onClick={handleLogout}
              startIcon={<LogoutIcon />}
              title={i18n.t("help.tooltips.logout")}
              sx={{ mr: 3 }}
            >
              {i18n.t("help.userSettings.logout")}
            </Button>
            <Button autoFocus color="inherit" type="submit">
              {i18n.t("help.userSettings.save")}
            </Button>
          </Toolbar>
        </AppBar>
        <Container maxWidth="sm">
          <Box sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="name"
              label={i18n.t("help.userSettings.name")}
              name="name"
              defaultValue={user?.name}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="id"
              label={i18n.t("help.userSettings.email")}
              name="id"
              defaultValue={user?.id}
              disabled
            />
            <Box component="fieldset" sx={{ px: 8, py: 2 }}>
              <legend>{i18n.t("help.userSettings.account")}:</legend>
              <Div>
                {i18n.t("help.userSettings.currentPackage") + " "}
                <span
                  style={{
                    color: "#00f",
                    fontWeight: "900",
                    fontSize: "1.2em",
                  }}
                >
                  {getCurrentPackage(user)}
                </span>
                {printPaypalSubscriptionText()}
              </Div>
              <Div>
                <FormControl fullWidth sx={{ mt: 1, mb: 1 }}>
                  <InputLabel id="language-label">{i18n.t("help.userSettings.language")}</InputLabel>
                  <Select
                    labelId="language-label"
                    id="language"
                    value={language}
                    label={i18n.t("help.userSettings.language")}
                    onChange={(event) => {
                      const newLanguage = event.target.value;
                      setLanguage(newLanguage);
                      setBettyLanguage(newLanguage);
                    }}
                  >
                    <MenuItem value="de">🇩🇪 {i18n.t("help.userSettings.german")}</MenuItem>
                    <MenuItem value="en">🇬🇧 {i18n.t("help.userSettings.english")}</MenuItem>
                  </Select>
                </FormControl>
              </Div>
              <Div>
                <FormControlLabel
                  control={
                    <Checkbox
                      defaultChecked
                      onChange={handleChangeTutorialActive}
                      checked={tutorialActive}
                    />
                  }
                  label={i18n.t("help.userSettings.showTutorialVideos")}
                />
              </Div>
            </Box>
            <Box
              component="fieldset"
              sx={{ px: 8, py: 2, textAlign: "-webkit-center" }}
              style={isAdmin ? { display: "block" } : { display: "none" }}
            >
              <legend>{i18n.t("help.userSettings.administration")}:</legend>
              <div>
                <YoutubeModal
                  title="Evaluate"
                  youtubeId="eVMMCRPPeMs"
                ></YoutubeModal>
                <YoutubeModal
                  title="Compilation mode"
                  youtubeId="abwRW_wUOzE"
                ></YoutubeModal>
              </div>
              <Button
                disabled={isDisabled}
                type="button"
                onClick={async () => {
                  setIsDisabled(true);
                  await fetchViaPost(
                    "share-team_properties",
                    JSON.stringify({
                      user_id_to_skip: "paule@paule-wetten.de",
                    }),
                  );
                  setIsDisabled(false);
                }}
                fullWidth
                style={isAdmin ? { display: "block" } : { display: "none" }}
                variant="contained"
                sx={{ mb: 2 }}
              >
                Sync team-properties for all users
              </Button>
              <Autocomplete
                id="country-select-demo"
                sx={{ width: 300 }}
                options={allUsers}
                autoHighlight
                getOptionLabel={(option) => option.label}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                    {...props}
                  >
                    {/*<img
                      loading="lazy"
                      width="20"
                      srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                      src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                      alt=""
                    />*/}
                    {option.label}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={i18n.t("help.userSettings.findUserToken")}
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: "new-password", // disable autocomplete and autofill
                    }}
                  />
                )}
                onChange={(event, value) => getUserToken(value.label)}
              />
            </Box>

            <Box component="fieldset" sx={{ px: 8, py: 2 }}>
              <legend>{i18n.t("help.userSettings.updateFactors")}</legend>
              <Typography
                title={i18n.t("help.tooltips.mainUpdateFactor")}
              >
                {i18n.t("help.userSettings.mainUpdateQuestion")}
              </Typography>
              <Slider
                id="mainUpdateFactor"
                label="Main update factor"
                name="mainUpdateFactor"
                sx={{ mt: 4 }}
                min={-100}
                max={-10}
                defaultValue={-user.main_update_factor}
                required
                valueLabelDisplay="on"
                marks={[
                  { value: -10, label: i18n.t("help.userSettings.maxImpact") },
                  { value: -100, label: i18n.t("help.userSettings.minImpact") },
                ]}
                valueLabelFormat={(value) =>
                  `1/${-value} (${(100 / -value).toFixed(2)} %)`
                }
                disabled={noMainUpdateFactor}
              />
              <FormControlLabel
                control={
                  <Checkbox /* checked={checked} onChange={handleChange} */ />
                }
                label="No impact"
                id="noMainUpdateFactor"
                name="noMainUpdateFactor"
                checked={noMainUpdateFactor}
                onChange={(e) => setNoMainUpdateFactor(e.target.checked)}
              />
              <Typography
                sx={{ mt: 4 }}
                title={i18n.t("help.tooltips.drawUpdateFactor")}
              >
                {i18n.t("help.userSettings.drawUpdateQuestion")}
              </Typography>
              <Slider
                id="drawUpdateFactor"
                label="Draw update factor"
                name="drawUpdateFactor"
                sx={{ mt: 4 }}
                min={-100}
                max={-30}
                defaultValue={-user.draw_update_factor}
                required
                valueLabelDisplay="on"
                marks={[
                  { value: -30, label: i18n.t("help.userSettings.maxImpact") },
                  { value: -100, label: i18n.t("help.userSettings.minImpact") },
                ]}
                valueLabelFormat={(value) =>
                  `1/${-value} (${(100 / -value).toFixed(2)} %)`
                }
                disabled={noDrawUpdateFactor}
              />
              <FormControlLabel
                control={
                  <Checkbox /* checked={checked} onChange={handleChange} */ />
                }
                label="No impact"
                id="noDrawUpdateFactor"
                name="noDrawUpdateFactor"
                checked={noDrawUpdateFactor}
                onChange={(e) => setNoDrawUpdateFactor(e.target.checked)}
              />
            </Box>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
            >
              {i18n.t("help.userSettings.save")}
            </Button>
            <Grid
              container
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            ></Grid>
          </Box>
        </Container>
      </form>
    </Dialog>
  );
}
